import { cn } from "@ascleo/ui/lib/utils";
import { useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ActiveBrandingSnapshot } from "./branding-types";

interface PracticeBrandScopeProps {
  readonly active: ActiveBrandingSnapshot;
  readonly className?: string;
  readonly children: ReactNode;
}

/**
 * Constrain practice-controlled variables and fonts to one patient-owned subtree.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <PracticeBrandScope active={active}><PatientJourney /></PracticeBrandScope>
 * ```
 */
export function PracticeBrandScope({
  active,
  className,
  children,
}: PracticeBrandScopeProps) {
  return (
    <div
      className={cn("frontdesk-practice-brand", className)}
      style={active.style}
      data-branding-version={active.manifest.publicationVersion}
    >
      {children}
    </div>
  );
}

interface IdentityProps {
  readonly active: ActiveBrandingSnapshot;
  readonly practiceName?: string;
  readonly className?: string;
}

/**
 * Resolved visual identity fields for a patient or constrained placement.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const identity = resolvePracticeIdentity(active, "Praxis Beispiel", "large");
 * ```
 */
export interface ResolvedPracticeIdentity {
  readonly name: string;
  readonly imageUrl?: string;
}

/**
 * Resolve the display-only name and fitting image without touching legal consumers.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * resolvePracticeIdentity(active, "Praxis Beispiel", "compact").name;
 * ```
 */
export function resolvePracticeIdentity(
  active: ActiveBrandingSnapshot,
  practiceName: string | undefined,
  placement: "large" | "compact",
): ResolvedPracticeIdentity {
  const name = active.visualDisplayName || practiceName || "FrontDesk+";
  const imageUrl =
    placement === "compact"
      ? active.compactMarkUrl ?? active.primaryLogoUrl
      : active.primaryLogoUrl;
  return imageUrl === undefined ? { name } : { name, imageUrl };
}

function Identity({
  active,
  practiceName,
  className,
  compact,
}: IdentityProps & { readonly compact: boolean }) {
  const [failedUrl, setFailedUrl] = useState<string>();
  const { name, imageUrl: url } = resolvePracticeIdentity(
    active,
    practiceName,
    compact ? "compact" : "large",
  );

  return (
    <figure
      className={cn(
        "m-0 flex min-w-0 max-w-full items-center overflow-hidden",
        compact ? "gap-2" : "gap-3",
        className,
      )}
      aria-label={name}
      data-testid={compact ? "frontdesk-practice-identity-compact" : "frontdesk-practice-identity"}
    >
      {url !== undefined && failedUrl !== url ? (
        // oxlint-disable-next-line next/no-img-element -- runtime-created verified blob URLs cannot use Next image optimization
        <img
          src={url}
          alt=""
          aria-hidden="true"
          className={cn(
            "shrink-0 object-contain object-left",
            compact
              ? "h-8 max-w-28"
              : "h-9 max-w-32 @3xl/kiosk-header:h-12 @3xl/kiosk-header:max-w-48",
          )}
          onError={() => setFailedUrl(url)}
        />
      ) : null}
      <span
        aria-hidden="true"
        dir="auto"
        className={cn(
          "min-w-0 text-start font-heading font-semibold text-primary-ink",
          compact
            ? "max-w-40 truncate text-sm"
            : // At most two lines in the kiosk header; a longer name is
              // clamped instead of growing the header and pushing the step
              // content down. Staff can publish a shorter display name.
              "line-clamp-2 max-w-full text-base leading-tight @3xl/kiosk-header:text-xl",
        )}
      >
        {name}
      </span>
    </figure>
  );
}

/**
 * Render the primary practice identity for patient-facing headers and welcome surfaces.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <PracticeIdentity active={active} practiceName="Praxis Beispiel" />
 * ```
 */
export function PracticeIdentity(props: IdentityProps) {
  return <Identity {...props} compact={false} />;
}

/**
 * Render the constrained practice identity for setup and operator-owned chrome.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <CompactPracticeIdentity active={active} practiceName="Praxis Beispiel" />
 * ```
 */
export function CompactPracticeIdentity(props: IdentityProps) {
  return <Identity {...props} compact />;
}

/**
 * Render restrained product attribution beneath the practice-first identity hierarchy.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <FrontDeskAttribution />
 * ```
 */
export function FrontDeskAttribution({ className }: { readonly className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      aria-hidden="true"
      className={cn(
        "text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground",
        className,
      )}
    >
      {t("branding.frontdesk-attribution", {
        defaultValue: "Mit FrontDesk+",
      })}
    </span>
  );
}
